import { motion } from "motion/react";
import { Star } from "lucide-react";

export interface Project {
  id: string;
  title: string;
  editedTime: string;
  createdAt?: string;
  thumbnail?: string;
  userAvatar?: string;
  userName?: string;
}

interface ProjectCardProps {
  project: Project;
  index: number;
  isStarred?: boolean;
  viewMode?: "small-grid" | "large-grid" | "list";
  onToggleStar?: (projectId: string) => void;
  onClick?: () => void;
}

export function ProjectCard({
  project,
  index,
  isStarred = false,
  viewMode = "large-grid",
  onToggleStar,
  onClick,
}: ProjectCardProps) {
  return (
    <motion.button
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{
        duration: 0.5,
        delay: 0.1 * index + 0.15,
        ease: [0.16, 1, 0.3, 1],
      }}
      className="group flex flex-col text-left cursor-pointer"
      onClick={onClick}
    >
      {viewMode === "list" ? (
        <div className="flex items-center w-full px-4 py-3 rounded-xl hover:bg-muted/30 transition-colors border border-transparent group-hover:border-border/50">
          {/* List View: Thumbnail */}
          <div className="relative h-12 w-20 shrink-0 rounded bg-muted overflow-hidden border border-border/50 mr-4">
            {project.thumbnail ? (
              <img
                src={project.thumbnail}
                alt={project.title}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="h-full w-full bg-linear-to-br from-muted to-muted/50" />
            )}
          </div>

          {/* List View: Title & Edited Time */}
          <div className="flex-2 min-w-0 pr-4">
            <h3 className="text-foreground text-sm font-medium truncate">
              {project.title}
            </h3>
            <p className="text-muted-foreground mt-0.5 text-xs truncate">
              {project.editedTime}
            </p>
          </div>

          {/* List View: Created At */}
          <div className="hidden md:block flex-1 min-w-0 pr-4">
            <p className="text-muted-foreground text-sm truncate">
              {project.createdAt}
            </p>
          </div>

          {/* List View: Created By */}
          <div className="hidden lg:flex flex-[1.5] items-center gap-2 min-w-0 pr-4 text-muted-foreground">
            {project.userAvatar ? (
              <img
                src={project.userAvatar}
                alt={project.userName || ""}
                className="h-6 w-6 rounded-full object-cover"
              />
            ) : (
              <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center text-[10px] font-medium text-muted-foreground uppercase">
                {project.userName?.[0] || "U"}
              </div>
            )}
            <span className="text-sm truncate">
              {project.userName || "Unknown user"}
            </span>
          </div>

          {/* List View: Star action */}
          <div className="shrink-0 w-10 flex justify-end">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleStar?.(project.id);
              }}
              className="flex size-8 items-center justify-center rounded-lg hover:bg-muted transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
              aria-label={isStarred ? "Unstar project" : "Star project"}
            >
              <Star
                className={`size-4 ${isStarred ? "fill-amber-400 text-amber-400 opacity-100" : "text-muted-foreground hover:text-foreground"}`}
              />
            </button>
            {isStarred && (
              <div className="absolute opacity-100 flex size-8 items-center justify-center pointer-events-none group-hover:opacity-0 transition-opacity">
                <Star className="size-4 fill-amber-400 text-amber-400" />
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* Grid View: Project thumbnail */}
          <div className="relative aspect-video w-full rounded-xl bg-muted overflow-hidden border border-border/50 transition-all group-hover:border-border">
            {project.thumbnail ? (
              <img
                src={project.thumbnail}
                alt={project.title}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="h-full w-full bg-linear-to-br from-muted to-muted/50" />
            )}

            {/* Grid View: Star button */}
            <div
              className={`absolute top-2 right-2 z-10 transition-opacity ${
                isStarred ? "opacity-100" : "opacity-0 group-hover:opacity-100"
              }`}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleStar?.(project.id);
                }}
                className="flex size-8 items-center justify-center rounded-lg bg-black/50 backdrop-blur-sm hover:bg-black/70 transition-colors"
                aria-label={isStarred ? "Unstar project" : "Star project"}
              >
                {isStarred ? (
                  <Star className="size-4 fill-amber-400 text-amber-400" />
                ) : (
                  <Star className="size-4 text-white/80" />
                )}
              </button>
            </div>
          </div>

          {/* Grid View: Project info */}
          <div className="mt-3 flex items-center gap-2 px-1">
            {project.userAvatar && (
              <div className="h-6 w-6 shrink-0 rounded-full bg-muted overflow-hidden mt-0.5">
                <img
                  src={project.userAvatar}
                  alt={project.userName || "User"}
                  className="h-full w-full object-cover"
                />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="text-foreground text-sm font-medium truncate">
                {project.title}
              </h3>
              <p className="text-muted-foreground mt-0.5 text-xs">
                {project.editedTime}
              </p>
            </div>
          </div>
        </>
      )}
    </motion.button>
  );
}

interface CreateProjectCardProps {
  onClick?: () => void;
  viewMode?: "small-grid" | "large-grid" | "list";
}

export function CreateProjectCard({
  onClick,
  viewMode = "large-grid",
}: CreateProjectCardProps) {
  return (
    <motion.button
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{
        duration: 0.5,
        delay: 0.15,
        ease: [0.16, 1, 0.3, 1],
      }}
      className="group flex flex-col text-left cursor-pointer"
      onClick={onClick}
    >
      {viewMode === "list" ? (
        <div className="flex items-center w-full px-4 py-3 rounded-xl border border-dashed border-border bg-muted/30 transition-all group-hover:border-foreground/50 group-hover:bg-muted/50">
          <div className="relative h-12 w-20 shrink-0 rounded bg-muted/50 flex items-center justify-center mr-4">
            <svg
              className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 4.5v15m7.5-7.5h-15"
              />
            </svg>
          </div>

          <div className="flex-2 min-w-0 pr-4">
            <h3 className="text-foreground text-sm font-medium">
              Create new project
            </h3>
          </div>

          {/* Empty placeholders to align columns */}
          <div className="hidden md:block flex-1 min-w-0 pr-4"></div>
          <div className="hidden lg:block flex-[1.5] min-w-0 pr-4"></div>
          <div className="shrink-0 w-10"></div>
        </div>
      ) : (
        <>
          {/* Dashed border create card */}
          <div className="aspect-video w-full rounded-xl border-2 border-dashed border-border bg-muted/30 transition-all group-hover:border-foreground/50 group-hover:bg-muted/50 flex items-center justify-center">
            <svg
              className="h-12 w-12 text-muted-foreground group-hover:text-foreground transition-colors"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 4.5v15m7.5-7.5h-15"
              />
            </svg>
          </div>

          {/* Label */}
          <div className="mt-3 px-1">
            <h3 className="text-foreground text-sm font-medium">
              Create new project
            </h3>
          </div>
        </>
      )}
    </motion.button>
  );
}
